import React, { useState } from "react";
import {
  Box, Typography, Card, CardContent, TextField, Button, Grid,
  Table, TableHead, TableRow, TableCell, TableBody, Paper, Chip, Divider, TableContainer,
} from "@mui/material";
import Swal from "sweetalert2";
import { motion } from "framer-motion";
import dayjs from "dayjs";

const brand = "#800b33";
const brandHover = "#6c0a2b";

const PenaltyWaiverRequests = () => {
  const [requests, setRequests] = useState([
    {
      id: 1,
      reason: "Medical emergency — admitted for 3 days",
      start: "2025-10-25",
      end: "2025-10-27",
      status: "Approved",
      approvedBy: "Katibu",
      date: "2025-10-27",
    },
  ]);

  const [form, setForm] = useState({ reason: "", start: "", end: "" });

  const handleSubmit = () => {
    if (!form.reason || !form.start || !form.end) {
      Swal.fire({
        icon: "warning",
        title: "Incomplete Form",
        text: "Please fill in all fields.",
        confirmButtonColor: brand,
      });
      return;
    }

    const newReq = {
      id: Date.now(),
      reason: form.reason,
      start: form.start,
      end: form.end,
      status: "Pending",
      date: dayjs().format("YYYY-MM-DD"),
    };

    setRequests((prev) => [newReq, ...prev]);
    setForm({ reason: "", start: "", end: "" });
    Swal.fire({
      icon: "success",
      title: "Request Submitted",
      text: "Your waiver request has been sent for review.",
      confirmButtonColor: brand,
    });
  };

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: (i = 1) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.1, duration: 0.4 },
    }),
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Typography variant="h5" sx={{ fontWeight: 900, color: brand, mb: 1 }}>
        Penalty Waiver Request
      </Typography>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 3 }}>
        Request a penalty exemption for valid reasons. Approval will be done by group officials.
      </Typography>

      {/* Form */}
      <motion.div variants={fadeIn} initial="hidden" animate="visible">
        <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 3 }}>
          <CardContent>
            <Typography sx={{ mb: 2, fontWeight: 700, color: brand }}>
              Waiver Request Form
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Reason for Waiver"
                  multiline
                  rows={3}
                  fullWidth
                  value={form.reason}
                  onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Start Date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={form.start}
                  onChange={(e) => setForm((f) => ({ ...f, start: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="End Date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={form.end}
                  onChange={(e) => setForm((f) => ({ ...f, end: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }}
                >
                  Submit Request
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </motion.div>

      {/* History */}
      <motion.div variants={fadeIn} initial="hidden" animate="visible">
        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <CardContent>
            <Typography sx={{ mb: 2, fontWeight: 700, color: brand }}>
              Request History & Status
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Approved By</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requests.map((r, i) => (
                    <TableRow key={r.id} hover>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{r.reason}</TableCell>
                      <TableCell>
                        {r.start} → {r.end}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={r.status}
                          color={
                            r.status === "Approved"
                              ? "success"
                              : r.status === "Rejected"
                              ? "error"
                              : "warning"
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{r.approvedBy || "-"}</TableCell>
                      <TableCell>{r.date}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </motion.div>
    </Box>
  );
};

export default PenaltyWaiverRequests;
